import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { useNavigate, useParams, useOutletContext } from 'react-router-dom';
import { gql, useQuery, useMutation, useApolloClient } from '@apollo/client';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import BasicDetails from './BasicDetails';
import BudgetDetails from './BudgetDetails';
import ScopeConfig, { ServiceState } from './ScopeConfig';
import AlertSettings from './AlertSettings';
import LabelSuccessToast from './LabelSuccessToast';
import { LabelFilterPair } from './LabelFilterMenu';

// --- TYPE DEFINITIONS ---
interface UserData {
  id: string;
  name: string;
  email: string;
  role: string;
  avatar?: string;
}
interface RoleData {
  name: string;
  description: string;
  icon: string;
  color: string;
}
type AlertRecipient = {
  data: { id?: string; name: string; [key: string]: any };
  type: 'role' | 'user';
};

interface Tag {
  label: string;
  value: string;
  id: string | number;
}

interface AppOutletContext {
  currentTenant?: { id: string; name: string; };
}

const staticRoles: RoleData[] = [
  { name: 'Admin', description: 'All current/ new users with admin role', icon: '/icons/admin.svg', color: '#FF8900' },
  { name: 'Editor', description: 'All current/ new users with editor role', icon: '/icons/editor.svg', color: '#49A7E4' },
  { name: 'Viewer', description: 'All current/ new users with viewer role', icon: '/icons/viewer.svg', color: '#666666' },
];

// --- QUERIES AND MUTATIONS ---
const GET_BUDGET_BY_ID = gql`
  query GetBudgetById($id: ID!) {
    budgetById(id: $id) {
      id
      name
      description
      tags
      provider
      period
      amount
      includeCredits
      metric # Added metric for LANGFUSE
      alertThresholds
      userIdsToNotify
      scopeConfigurations {
        id
        dataSources { id }
        projectIds
        regions
        services
        labels
        k8sClusters
        k8sNamespaces
        skus # Added skus for LANGFUSE
      }
    }
  }
`;

const GET_USERS_BY_ROLE = gql`
  query GetUsersByRoleInTenant($tenantId: ID!, $roles: [Role!]!) {
    getUsersByRoleInTenant(tenantId: $tenantId, roles: $roles) {
      id
      name
      email
      role
      photo
    }
  }
`;

const UPDATE_BUDGET_MUTATION = gql`
  mutation UpdateBudget($id: ID!, $input: UpdateBudgetInput!) {
    updateBudget(id: $id, input: $input) {
      id
      name
    }
  }
`;

// MODIFIED: Added skus to support LANGFUSE
const getInitialService = (): Omit<ServiceState, 'id' | 'serviceTypes'> & { serviceTypes: string[] } => ({
  dataSources: [],
  projectIds: [],
  regions: [],
  serviceTypes: [],
  label: [],
  cluster: [],
  namespace: [],
  deployment: [],
  skus: [],
});

const EditBudgetPage: React.FC = () => {
  const navigate = useNavigate();
  const { budgetId } = useParams<{ budgetId: string }>();
  const { currentTenant } = useOutletContext<AppOutletContext>();
  const paperRef = useRef(null);
  const client = useApolloClient();

  // --- STATE MANAGEMENT ---
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [budgetName, setBudgetName] = useState('');
  const [budgetDescription, setBudgetDescription] = useState('');
  const [tags, setTags] = useState<Tag[]>([]);
  const [provider, setProvider] = useState('');
  const [services, setServices] = useState<ServiceState[]>([]);
  const [period, setPeriod] = useState<'Monthly' | 'Quarterly' | 'Yearly'>('Monthly');
  const [currency, setCurrency] = useState<'INR' | 'USD'>('USD');
  const [amount, setAmount] = useState('');
  const [includeCredits, setIncludeCredits] = useState(false);
  const [thresholds, setThresholds] = useState<{ id: number; percentage: string; }[]>([]);
  const [alertRecipients, setAlertRecipients] = useState<AlertRecipient[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogLabel, setDialogLabel] = useState('');
  const [dialogTag, setDialogTag] = useState('');
  const [isToastOpen, setIsToastOpen] = useState(false);
  const [lastDeletedTag, setLastDeletedTag] = useState<Tag | null>(null);
  const [toastMessage, setToastMessage] = useState('');
  const [allUsers, setAllUsers] = useState<UserData[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [metric, setMetric] = useState<'COST' | 'TOKENS'>('COST'); // Added metric state

  // --- DATA FETCHING & PROCESSING ---

  useEffect(() => {
    if (!currentTenant?.id) return;
    const fetchAllUsers = async () => {
      setUsersLoading(true);
      const rolesToFetch = ['admin', 'editor', 'viewer'];
      try {
        const promises = rolesToFetch.map(role =>
          client.query({
            query: GET_USERS_BY_ROLE,
            variables: { tenantId: currentTenant.id, roles: [role] }
          })
        );
        const results = await Promise.all(promises);
        const combinedUsers = results.flatMap(result => result.data.getUsersByRoleInTenant);
        const uniqueUsers = Array.from(new Map(combinedUsers.map(user => [user.id, {
    ...user,
    avatar: user.photo
}])).values());
        setAllUsers(uniqueUsers);
      } catch (error) {
        console.error("Failed to fetch users for dropdown:", error);
        setAllUsers([]);
      } finally {
        setUsersLoading(false);
      }
    };
    fetchAllUsers();
  }, [currentTenant?.id, client]);

  const { data: budgetData, loading: queryLoading, error: queryError } = useQuery(GET_BUDGET_BY_ID, {
    variables: { id: budgetId },
    skip: !budgetId,
    onCompleted: (data) => {
      if (data && data.budgetById) {
        const budgetToEdit = data.budgetById;
        setBudgetName(budgetToEdit.name || '');
        setBudgetDescription(budgetToEdit.description || '');
        const tagsArray = budgetToEdit.tags
          ? Object.entries(budgetToEdit.tags).map(([label, value], index): Tag => ({
              id: `loaded_${label}_${index}`,
              label,
              value: String(value),
            }))
          : [];
        setTags(tagsArray);
        setProvider(budgetToEdit.provider || '');
        
        const mappedServices = budgetToEdit.scopeConfigurations.map((sc: any, index: number) => {
            const groupedLabels = (sc.labels || []).reduce((acc: {[key: string]: string[]}, label: {key: string, value: string}) => {
                if (!acc[label.key]) acc[label.key] = [];
                acc[label.key].push(label.value);
                return acc;
            }, {});

            const serviceLabels = Object.entries(groupedLabels).map(([key, values], idx) => ({
                id: `${key}_${idx}`, key, values, source: 'existing'
            }));

            return {
                id: sc.id || `service_${Date.now() + index}`,
                dataSources: sc.dataSources?.map((ds: { id: string }) => ds.id) || [],
                projectIds: sc.projectIds || [],
                regions: sc.regions || [],
                serviceTypes: sc.services || [],
                label: serviceLabels,
                cluster: sc.k8sClusters || [],
                namespace: sc.k8sNamespaces || [],
                deployment: [],
                skus: sc.skus || [], // Populate skus
            };
        });

        setServices(mappedServices);
        setPeriod(budgetToEdit.period?.charAt(0).toUpperCase() + budgetToEdit.period?.slice(1).toLowerCase() as 'Monthly' | 'Quarterly' | 'Yearly' || 'Monthly');
        setAmount(budgetToEdit.amount?.toString() || '');
        setIncludeCredits(budgetToEdit.includeCredits || false);
        setMetric(budgetToEdit.metric || 'COST'); // Populate metric
        setThresholds(budgetToEdit.alertThresholds?.map((p: any, i: number) => ({ id: Date.now() + i, percentage: p.toString() })) || []);
      } else {
        navigate('/budget-alerts');
      }
    }
  });

  useEffect(() => {
    const savedRecipientIds = budgetData?.budgetById?.userIdsToNotify;
    if (savedRecipientIds && allUsers.length > 0) {
      const initialRecipients = savedRecipientIds.map((idOrRole: string) => {
        if (idOrRole.startsWith('ROLE:')) {
          const roleName = idOrRole.split(':')[1];
          const roleData = staticRoles.find(
            (r) => r.name.toLowerCase() === roleName.toLowerCase()
          );
          return roleData ? { type: 'role' as const, data: roleData } : null;
        } else {
          const userData = allUsers.find(u => u.id === idOrRole);
          return userData ? { type: 'user' as const, data: userData } : null;
        }
      }).filter((item: AlertRecipient | null): item is AlertRecipient => item !== null);
      
      setAlertRecipients(initialRecipients);
    }
  }, [budgetData, allUsers]);

  const [updateBudget, { loading: mutationLoading }] = useMutation(UPDATE_BUDGET_MUTATION);

  // --- HANDLER FUNCTIONS ---
  const isBudgetDetailsComplete = period && amount.trim() !== '';

  useEffect(() => {
    const newErrors = { ...errors };
    let hasErrorChanged = false;

    if (amount.trim() && newErrors.amount) {
        delete newErrors.amount;
        hasErrorChanged = true;
    }
    if (provider && newErrors.provider) {
        delete newErrors.provider;
        hasErrorChanged = true;
    }
    if (services.length > 0 && (newErrors.dataSources || newErrors.regions || newErrors.services)) {
        delete newErrors.dataSources;
        delete newErrors.regions;
        delete newErrors.services;
        hasErrorChanged = true;
    }
    if (alertRecipients.length > 0 && newErrors.alertRecipients) {
        delete newErrors.alertRecipients;
        hasErrorChanged = true;
    }

    if (hasErrorChanged) {
        setErrors(newErrors);
    }
  }, [amount, provider, services, alertRecipients, tags, errors]);

  const handleBudgetNameChange = (name: string) => {
    setBudgetName(name);
    const newErrors = { ...errors };

    if (newErrors.budgetName) {
        delete newErrors.budgetName;
    }

    if (!/^[a-zA-Z0-9\s_-]*$/.test(name)) {
      newErrors.budgetName = 'Name can only contain letters, numbers, spaces, -, and _.';
    }
    setErrors(newErrors);
  };

  const handleBudgetDescriptionChange = (description: string) => {
    setBudgetDescription(description);
    const newErrors = { ...errors };

    if(newErrors.budgetDescription) {
        delete newErrors.budgetDescription;
    }

    if (description.length > 200) {
      newErrors.budgetDescription = 'Description cannot exceed 200 characters.';
    }
    setErrors(newErrors);
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    const trimmedName = budgetName.trim();
    if (!trimmedName) {
      newErrors.budgetName = 'Budget Name is required.';
    } else if (trimmedName.length < 3) {
      newErrors.budgetName = 'Name must be at least 3 characters.';
    } else if (trimmedName.length > 50) {
      newErrors.budgetName = 'Name cannot exceed 50 characters.';
    } else if (!/^[a-zA-Z0-9\s_-]+$/.test(trimmedName)) {
      newErrors.budgetName = 'Name can only contain letters, numbers, spaces, -, and _.';
    }

    const trimmedDescription = budgetDescription.trim();
    if (!trimmedDescription) {
      newErrors.budgetDescription = 'Budget Description is required.';
    } else if (trimmedDescription.length > 200) {
      newErrors.budgetDescription = 'Description cannot exceed 200 characters.';
    }

    if (!provider) newErrors.provider = 'A Cloud Provider must be selected.';
    if (services.length === 0 || !services.every(s => s.dataSources.length > 0)) newErrors.dataSources = 'Each service must have a Data Source.';
    
    if (provider === 'GCP') {
        if (!services.every(s => s.regions.length > 0)) newErrors.regions = 'Each service must have a Region.';
        if (!services.every(s => s.serviceTypes.length > 0)) newErrors.services = 'Each service must have a Service Type configured.';
    }

    if (!amount.trim()) newErrors.amount = 'Budget Amount is required.';
    if (alertRecipients.length === 0) newErrors.alertRecipients = 'At least one Alert Recipient is required.';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveChanges = async () => {
    if (!validateForm() || !budgetId) return;

    const updateBudgetInput = {
      name: budgetName,
      description: budgetDescription,
      tags: tags.reduce((obj, item) => ({ ...obj, [item.label]: item.value }), {}),
      period: period.toUpperCase() as 'MONTHLY' | 'QUARTERLY' | 'YEARLY',
      amount: parseFloat(amount.replace(/,/g, '')),
      includeCredits: provider === 'LANGFUSE' ? false : includeCredits,
      metric: provider === 'LANGFUSE' ? metric : "COST",
      alertThresholds: thresholds.map(t => parseInt(t.percentage, 10)).filter(t => !isNaN(t)),
      userIdsToNotify: alertRecipients.map(recipient => {
        if (recipient.type === 'role') {
          return `ROLE:${recipient.data.name.toLowerCase()}`;
        }
        return recipient.data.id as string;
      }),
      scopeConfigurations: services.map(service => ({
        dataSourceIds: service.dataSources,
        projectIds: provider === 'GCP' ? service.projectIds : [],
        regions: provider === 'GCP' ? service.regions : [],
        services: service.serviceTypes,
        labels: provider === 'GCP' ? service.label.flatMap((l: LabelFilterPair) =>
            l.values.map((v: string) => ({ key: l.key, value: v }))
        ) : [],
        k8sClusters: provider === 'GCP' ? service.cluster : [],
        k8sNamespaces: provider === 'GCP' ? service.namespace : [],
        skus: provider === 'LANGFUSE' ? service.skus : [],
      })),
    };

    try {
      const result = await updateBudget({
        variables: { id: budgetId, input: updateBudgetInput },
      });

      if (result.data?.updateBudget) {
        navigate('/budget-alerts', {
          replace: true,
          state: { budgetUpdated: true, budgetName: result.data.updateBudget.name }
        });
      } else {
        throw new Error("Received no data from server after update.");
      }
    } catch (err: any) {
      console.error("Failed to update budget:", err);
      setToastMessage(`Error: ${err.message || 'Could not save changes.'}`);
      setIsToastOpen(true);
    }
  };

  const handleAddTag = () => {
    const newTag: Tag = { id: Date.now(), label: dialogLabel, value: dialogTag };
    if (newTag.label && newTag.value && !tags.some(tag => tag.label === newTag.label && tag.value === newTag.value)) {
      setTags(prevTags => [...prevTags, newTag]);
    }
    setIsDialogOpen(false);
    setDialogLabel('');
    setDialogTag('');
  };

  const handleDeleteTag = (tagToDelete: Tag) => {
    setLastDeletedTag(tagToDelete);
    setTags(prevTags => prevTags.filter(tag => tag.id !== tagToDelete.id));
    setToastMessage("Label deleted successfully");
    setIsToastOpen(true);
  };

  const handleUndo = () => {
    if (lastDeletedTag) {
      setTags(prevTags => [...prevTags, lastDeletedTag]);
      setLastDeletedTag(null);
      setIsToastOpen(false);
    }
  };

  const handleToastClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setIsToastOpen(false);
    setLastDeletedTag(null);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setDialogLabel('');
    setDialogTag('');
  };

  if (queryLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (queryError) {
    return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="error" variant="h6" gutterBottom>Error Loading Budget</Typography>
            <Typography color="error">Could not fetch budget details. Please try again later.</Typography>
            <pre style={{ whiteSpace: 'pre-wrap', textAlign: 'left', backgroundColor: '#fdecea', border: '1px solid #f44336', padding: '1rem', marginTop: '1rem', borderRadius: '8px' }}>
                {queryError.message}
            </pre>
            <Button sx={{mt: 2}} variant="contained" onClick={() => navigate('/budget-alerts')}>Go Back</Button>
        </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 0 }, height: '100%', boxSizing: 'border-box', backgroundColor: '#F8F9FA' }}>
      <LabelSuccessToast
        open={isToastOpen}
        onClose={handleToastClose}
        message={toastMessage}
        onUndo={lastDeletedTag ? handleUndo : () => {}}
      />
      
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: -1 }}>
          <IconButton onClick={() => navigate('/budget-alerts')} size="small" sx={{ color: '#333333', ml: -1 }}>
            <ArrowBackIcon sx={{ width: '20px', height: '20px' }} />
          </IconButton>
          <Typography variant="body2" color="#333333" sx={{ fontWeight: 600, fontFamily: 'Inter', fontSize: '14px' }}>
            Edit Budget
          </Typography>
        </Box>
        <Typography variant="body2" color="#666666" sx={{ fontWeight: 400, fontFamily: 'Inter', fontSize: '12px', ml: 2.5, mt: -1 }}>
          Edit the below details to update the budget
        </Typography>
      </Box>
      
      <Box component="div" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <BasicDetails
          budgetName={budgetName}
          onBudgetNameChange={handleBudgetNameChange}
          budgetDescription={budgetDescription}
          onBudgetDescriptionChange={handleBudgetDescriptionChange}
          tags={tags}
          handleDeleteTag={handleDeleteTag}
          setIsDialogOpen={setIsDialogOpen}
          errors={errors}
        />
        <ScopeConfig
          provider={provider}
          setProvider={setProvider}
          services={services}
          setServices={setServices}
          getInitialService={getInitialService}
          errors={errors}
          disabled={true}
          period={period}
        />
        <BudgetDetails
          period={period}
          setPeriod={setPeriod}
          currency={currency}
          setCurrency={setCurrency}
          amount={amount}
          setAmount={setAmount}
          includeCredits={includeCredits}
          setIncludeCredits={setIncludeCredits}
          thresholds={thresholds}
          setThresholds={setThresholds}
          errors={errors}
          provider={provider}
          metric={metric}
          setMetric={setMetric}
        />
        <AlertSettings
          recipients={alertRecipients}
          setRecipients={setAlertRecipients}
          errors={errors}
          roles={staticRoles}
          allUsers={allUsers}
          isLoading={usersLoading || queryLoading}
        />
        <Box sx={{ p: 2, bgcolor: '#FFFFFF', borderRadius: '10px', border: '1px solid #F3F3F3', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => navigate('/budget-alerts')} sx={{ width: '79px', height: '36px', borderRadius: '8px', backgroundColor: '#F2F7FF', color: '#0064FF', fontFamily: 'Inter', fontWeight: 500, fontSize: '14px', textTransform: 'none', '&:hover': { backgroundColor: '#E0EFFF' } }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveChanges}
              disabled={mutationLoading}
              sx={{ width: '130px', height: '36px', borderRadius: '8px', backgroundColor: '#0064FF', color: 'white', fontFamily: 'Inter', fontWeight: 500, fontSize: '14px', textTransform: 'none', '&:hover': { backgroundColor: '#0053D9' } }}
            >
              {mutationLoading ? <CircularProgress size={24} color="inherit" /> : 'Save Changes'}
            </Button>
          </Box>
        </Box>
      </Box>
      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        PaperProps={{
          ref: paperRef,
          sx: {
            borderRadius: '12px', width: '600px', height: '322px', maxWidth: '600px', overflow: 'hidden',
            backgroundImage: `url('/textures/Texture.svg'), url('/textures/Gradient_Popup.svg')`,
            backgroundRepeat: 'no-repeat, no-repeat', backgroundPosition: 'top right, center', backgroundSize: 'auto, cover',
          }
        }}
      >
       <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <img src="/icons/add-filter-icon1.svg" alt="Add" style={{ width: '56px', height: '56px', marginRight: '16px' }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '20px', color: '#333333', fontFamily: 'Inter' }}>Add Label/ Tag</Typography>
              <Typography variant="body2" sx={{ fontWeight: 400,fontSize: '14px', color: '#666666', fontFamily: 'Inter' }}>Add label details to setup alerts.</Typography>
            </Box>
          </Box>
          <DialogContent sx={{ p: '0 !important', mt: 7, mb: 3, flexGrow: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 1, fontFamily: 'Inter', fontSize: '14px',fontWeight: 400 }}>Key<span style={{color: 'red'}}>*</span></Typography>
                <TextField fullWidth variant="outlined" placeholder="Enter label" value={dialogLabel} onChange={(e) => setDialogLabel(e.target.value)} size="small" InputProps={{ endAdornment: ( <InputAdornment position="end"> {dialogLabel && ( <CheckIcon sx={{ color: '#03A65A', fontSize: '15px' }} /> )} </InputAdornment> ) }} sx={{ '& .MuiOutlinedInput-root': { height: '40px', borderRadius: '8px', fontFamily: 'Inter', backgroundColor: '#FFFFFF', '& .MuiInputBase-input:-webkit-autofill': { WebkitBoxShadow: '0 0 0 100px #FFFFFF inset' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#E0E0E0', }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0064FF',borderWidth: '1px' } }, '& .MuiInputBase-input::placeholder': { fontSize: '14px', fontFamily: 'Inter', }, '& .MuiInputBase-input': { fontFamily: 'Inter', fontSize: '14px', }, }}/>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 1, fontFamily: 'Inter', fontSize: '14px',fontWeight: 400 }}>Value<span style={{color: 'red'}}>*</span></Typography>
                <TextField fullWidth variant="outlined" placeholder="Enter tag" value={dialogTag} onChange={(e) => setDialogTag(e.target.value)} size="small" InputProps={{ endAdornment: ( <InputAdornment position="end"> {dialogTag && ( <CheckIcon sx={{ color: '#03A65A', fontSize: '15px' }} /> )} </InputAdornment> ) }} sx={{ '& .MuiOutlinedInput-root': { height: '40px', borderRadius: '8px', fontFamily: 'Inter', backgroundColor: '#FFFFFF', '& .MuiInputBase-input:-webkit-autofill': { WebkitBoxShadow: '0 0 0 100px #FFFFFF inset' }, '& fieldset': { borderColor: '#E0E0E0', }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#E0E0E0' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0064FF',borderWidth: '1px' } }, '& .MuiInputBase-input::placeholder': { fontSize: '14px', fontFamily: 'Inter', }, '& .MuiInputBase-input': { fontFamily: 'Inter', fontSize: '14px',}, }}/>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 0 }}>
            <Button onClick={handleCloseDialog} sx={{ width:"79px", height:"36px",textTransform: 'none', borderRadius: '8px', color: '#0064FF', backgroundColor: '#F2F7FF', fontFamily: 'Inter', fontSize:"14px", fontWeight:500 }}>Cancel</Button>
            <Button variant="contained" onClick={handleAddTag} disabled={!dialogLabel || !dialogTag} sx={{ width:"74px", height:"36px",textTransform: 'none', borderRadius: '8px', backgroundColor: '#0064FF', fontFamily: 'Inter', fontSize:"14px", fontWeight:500 }}> Add </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
};

export default EditBudgetPage;


// import React, { useState, useEffect, useRef } from 'react';
// import {
//   Box,
//   Typography,
//   Button,
//   IconButton,
//   TextField,
//   Dialog,
//   DialogActions,
//   DialogContent,
//   InputAdornment,
//   CircularProgress,
// } from '@mui/material';
// import { useNavigate, useParams, useOutletContext } from 'react-router-dom';
// import { gql, useQuery, useMutation, useApolloClient } from '@apollo/client';
// import ArrowBackIcon from '@mui/icons-material/ArrowBack';
// import CheckIcon from '@mui/icons-material/Check';
// import BasicDetails from './BasicDetails';
// import BudgetDetails from './BudgetDetails';
// import ScopeConfig, { ServiceState } from './ScopeConfig';
// import AlertSettings from './AlertSettings';
// import LabelSuccessToast from './LabelSuccessToast';
// import { LabelFilterPair } from './LabelFilterMenu';

// // --- TYPE DEFINITIONS ---
// interface UserData {
//   id: string;
//   name: string;
//   email: string;
//   role: string;
//   avatar?: string;
// }
// interface RoleData {
//   name: string;
//   description: string;
//   icon: string;
//   color: string;
// }
// type AlertRecipient = {
//   data: { id?: string; name: string; [key: string]: any };
//   type: 'role' | 'user';
// };

// interface Tag {
//   label: string;
//   value: string;
//   id: string | number;
// }

// interface AppOutletContext {
//   currentTenant?: { id: string; name: string; };
// }

// const staticRoles: RoleData[] = [
//   { name: 'Admin', description: 'All current/ new users with admin role', icon: '/icons/admin.svg', color: '#FF8900' },
//   { name: 'Editor', description: 'All current/ new users with editor role', icon: '/icons/editor.svg', color: '#49A7E4' },
//   { name: 'Viewer', description: 'All current/ new users with viewer role', icon: '/icons/viewer.svg', color: '#666666' },
// ];

// // --- QUERIES AND MUTATIONS ---
// const GET_BUDGET_BY_ID = gql`
//   query GetBudgetById($id: ID!) {
//     budgetById(id: $id) {
//       id
//       name
//       description
//       tags
//       provider
//       period
//       amount
//       includeCredits
//       metric # Added metric for LANGFUSE
//       alertThresholds
//       userIdsToNotify
//       scopeConfigurations {
//         id
//         dataSources { id }
//         projectIds
//         regions
//         services
//         labels
//         k8sClusters
//         k8sNamespaces
//         skus # Added skus for LANGFUSE
//       }
//     }
//   }
// `;

// const GET_USERS_BY_ROLE = gql`
//   query GetUsersByRoleInTenant($tenantId: ID!, $roles: [Role!]!) {
//     getUsersByRoleInTenant(tenantId: $tenantId, roles: $roles) {
//       id
//       name
//       email
//       role
//       photo
//     }
//   }
// `;

// const UPDATE_BUDGET_MUTATION = gql`
//   mutation UpdateBudget($id: ID!, $input: UpdateBudgetInput!) {
//     updateBudget(id: $id, input: $input) {
//       id
//       name
//     }
//   }
// `;

// // MODIFIED: Added skus to support LANGFUSE
// const getInitialService = (): Omit<ServiceState, 'id' | 'serviceTypes'> & { serviceTypes: string[] } => ({
//   dataSources: [],
//   projectIds: [],
//   regions: [],
//   serviceTypes: [],
//   label: [],
//   cluster: [],
//   namespace: [],
//   deployment: [],
//   skus: [],
// });

// const EditBudgetPage: React.FC = () => {
//   const navigate = useNavigate();
//   const { budgetId } = useParams<{ budgetId: string }>();
//   const { currentTenant } = useOutletContext<AppOutletContext>();
//   const paperRef = useRef(null);
//   const client = useApolloClient();

//   // --- STATE MANAGEMENT ---
//   const [errors, setErrors] = useState<{ [key: string]: string }>({});
//   const [budgetName, setBudgetName] = useState('');
//   const [budgetDescription, setBudgetDescription] = useState('');
//   const [tags, setTags] = useState<Tag[]>([]);
//   const [provider, setProvider] = useState('');
//   const [services, setServices] = useState<ServiceState[]>([]);
//   const [period, setPeriod] = useState<'Monthly' | 'Quarterly' | 'Yearly'>('Monthly');
//   const [currency, setCurrency] = useState<'INR' | 'USD'>('USD');
//   const [amount, setAmount] = useState('');
//   const [includeCredits, setIncludeCredits] = useState(false);
//   const [thresholds, setThresholds] = useState<{ id: number; percentage: string; }[]>([]);
//   const [alertRecipients, setAlertRecipients] = useState<AlertRecipient[]>([]);
//   const [isDialogOpen, setIsDialogOpen] = useState(false);
//   const [dialogLabel, setDialogLabel] = useState('');
//   const [dialogTag, setDialogTag] = useState('');
//   const [isToastOpen, setIsToastOpen] = useState(false);
//   const [lastDeletedTag, setLastDeletedTag] = useState<Tag | null>(null);
//   const [toastMessage, setToastMessage] = useState('');
//   const [allUsers, setAllUsers] = useState<UserData[]>([]);
//   const [usersLoading, setUsersLoading] = useState(true);
//   const [metric, setMetric] = useState<'COST' | 'TOKENS'>('COST'); // Added metric state

//   // --- DATA FETCHING & PROCESSING ---

//   useEffect(() => {
//     if (!currentTenant?.id) return;
//     const fetchAllUsers = async () => {
//       setUsersLoading(true);
//       const rolesToFetch = ['admin', 'editor', 'viewer'];
//       try {
//         const promises = rolesToFetch.map(role =>
//           client.query({
//             query: GET_USERS_BY_ROLE,
//             variables: { tenantId: currentTenant.id, roles: [role] }
//           })
//         );
//         const results = await Promise.all(promises);
//         const combinedUsers = results.flatMap(result => result.data.getUsersByRoleInTenant);
//         const uniqueUsers = Array.from(new Map(combinedUsers.map(user => [user.id, {
//     ...user,
//     avatar: user.photo
// }])).values());
//         setAllUsers(uniqueUsers);
//       } catch (error) {
//         console.error("Failed to fetch users for dropdown:", error);
//         setAllUsers([]);
//       } finally {
//         setUsersLoading(false);
//       }
//     };
//     fetchAllUsers();
//   }, [currentTenant?.id, client]);

//   const { data: budgetData, loading: queryLoading, error: queryError } = useQuery(GET_BUDGET_BY_ID, {
//     variables: { id: budgetId },
//     skip: !budgetId,
//     onCompleted: (data) => {
//       if (data && data.budgetById) {
//         const budgetToEdit = data.budgetById;
//         setBudgetName(budgetToEdit.name || '');
//         setBudgetDescription(budgetToEdit.description || '');
//         const tagsArray = budgetToEdit.tags
//           ? Object.entries(budgetToEdit.tags).map(([label, value], index): Tag => ({
//               id: `loaded_${label}_${index}`,
//               label,
//               value: String(value),
//             }))
//           : [];
//         setTags(tagsArray);
//         setProvider(budgetToEdit.provider || '');
        
//         const mappedServices = budgetToEdit.scopeConfigurations.map((sc: any, index: number) => {
//             const groupedLabels = (sc.labels || []).reduce((acc: {[key: string]: string[]}, label: {key: string, value: string}) => {
//                 if (!acc[label.key]) acc[label.key] = [];
//                 acc[label.key].push(label.value);
//                 return acc;
//             }, {});

//             const serviceLabels = Object.entries(groupedLabels).map(([key, values], idx) => ({
//                 id: `${key}_${idx}`, key, values, source: 'existing'
//             }));

//             return {
//                 id: sc.id || `service_${Date.now() + index}`,
//                 dataSources: sc.dataSources?.map((ds: { id: string }) => ds.id) || [],
//                 projectIds: sc.projectIds || [],
//                 regions: sc.regions || [],
//                 serviceTypes: sc.services || [],
//                 label: serviceLabels,
//                 cluster: sc.k8sClusters || [],
//                 namespace: sc.k8sNamespaces || [],
//                 deployment: [],
//                 skus: sc.skus || [], // Populate skus
//             };
//         });

//         setServices(mappedServices);
//         setPeriod(budgetToEdit.period?.charAt(0).toUpperCase() + budgetToEdit.period?.slice(1).toLowerCase() as 'Monthly' | 'Quarterly' | 'Yearly' || 'Monthly');
//         setAmount(budgetToEdit.amount?.toString() || '');
//         setIncludeCredits(budgetToEdit.includeCredits || false);
//         setMetric(budgetToEdit.metric || 'COST'); // Populate metric
//         setThresholds(budgetToEdit.alertThresholds?.map((p: any, i: number) => ({ id: Date.now() + i, percentage: p.toString() })) || []);
//       } else {
//         navigate('/budget-alerts');
//       }
//     }
//   });

//   useEffect(() => {
//     const savedRecipientIds = budgetData?.budgetById?.userIdsToNotify;
//     if (savedRecipientIds && allUsers.length > 0) {
//       const initialRecipients = savedRecipientIds.map((idOrRole: string) => {
//         if (idOrRole.startsWith('ROLE:')) {
//           const roleName = idOrRole.split(':')[1];
//           const roleData = staticRoles.find(
//             (r) => r.name.toLowerCase() === roleName.toLowerCase()
//           );
//           return roleData ? { type: 'role' as const, data: roleData } : null;
//         } else {
//           const userData = allUsers.find(u => u.id === idOrRole);
//           return userData ? { type: 'user' as const, data: userData } : null;
//         }
//       }).filter((item: AlertRecipient | null): item is AlertRecipient => item !== null);
      
//       setAlertRecipients(initialRecipients);
//     }
//   }, [budgetData, allUsers]);

//   const [updateBudget, { loading: mutationLoading }] = useMutation(UPDATE_BUDGET_MUTATION);

//   // --- HANDLER FUNCTIONS ---
//   const isBudgetDetailsComplete = period && amount.trim() !== '';

//   useEffect(() => {
//     const newErrors = { ...errors };
//     let hasErrorChanged = false;

//     if (amount.trim() && newErrors.amount) {
//         delete newErrors.amount;
//         hasErrorChanged = true;
//     }
//     if (provider && newErrors.provider) {
//         delete newErrors.provider;
//         hasErrorChanged = true;
//     }
//     if (services.length > 0 && (newErrors.dataSources || newErrors.regions || newErrors.services)) {
//         delete newErrors.dataSources;
//         delete newErrors.regions;
//         delete newErrors.services;
//         hasErrorChanged = true;
//     }
//     if (alertRecipients.length > 0 && newErrors.alertRecipients) {
//         delete newErrors.alertRecipients;
//         hasErrorChanged = true;
//     }

//     if (hasErrorChanged) {
//         setErrors(newErrors);
//     }
//   }, [amount, provider, services, alertRecipients, tags, errors]);

//   const handleBudgetNameChange = (name: string) => {
//     setBudgetName(name);
//     const newErrors = { ...errors };

//     if (newErrors.budgetName) {
//         delete newErrors.budgetName;
//     }

//     if (!/^[a-zA-Z0-9\s_-]*$/.test(name)) {
//       newErrors.budgetName = 'Name can only contain letters, numbers, spaces, -, and _.';
//     }
//     setErrors(newErrors);
//   };

//   const handleBudgetDescriptionChange = (description: string) => {
//     setBudgetDescription(description);
//     const newErrors = { ...errors };

//     if(newErrors.budgetDescription) {
//         delete newErrors.budgetDescription;
//     }

//     if (description.length > 200) {
//       newErrors.budgetDescription = 'Description cannot exceed 200 characters.';
//     }
//     setErrors(newErrors);
//   };

//   const validateForm = () => {
//     const newErrors: { [key: string]: string } = {};

//     const trimmedName = budgetName.trim();
//     if (!trimmedName) {
//       newErrors.budgetName = 'Budget Name is required.';
//     } else if (trimmedName.length < 3) {
//       newErrors.budgetName = 'Name must be at least 3 characters.';
//     } else if (trimmedName.length > 50) {
//       newErrors.budgetName = 'Name cannot exceed 50 characters.';
//     } else if (!/^[a-zA-Z0-9\s_-]+$/.test(trimmedName)) {
//       newErrors.budgetName = 'Name can only contain letters, numbers, spaces, -, and _.';
//     }

//     const trimmedDescription = budgetDescription.trim();
//     if (!trimmedDescription) {
//       newErrors.budgetDescription = 'Budget Description is required.';
//     } else if (trimmedDescription.length > 200) {
//       newErrors.budgetDescription = 'Description cannot exceed 200 characters.';
//     }

//     if (!provider) newErrors.provider = 'A Cloud Provider must be selected.';
//     if (services.length === 0 || !services.every(s => s.dataSources.length > 0)) newErrors.dataSources = 'Each service must have a Data Source.';
    
//     if (provider === 'GCP') {
//         if (!services.every(s => s.regions.length > 0)) newErrors.regions = 'Each service must have a Region.';
//         if (!services.every(s => s.serviceTypes.length > 0)) newErrors.services = 'Each service must have a Service Type configured.';
//     }

//     if (!amount.trim()) newErrors.amount = 'Budget Amount is required.';
//     if (alertRecipients.length === 0) newErrors.alertRecipients = 'At least one Alert Recipient is required.';

//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   const handleSaveChanges = async () => {
//     if (!validateForm() || !budgetId) return;

//     const updateBudgetInput = {
//       name: budgetName,
//       description: budgetDescription,
//       tags: tags.reduce((obj, item) => ({ ...obj, [item.label]: item.value }), {}),
//       period: period.toUpperCase() as 'MONTHLY' | 'QUARTERLY' | 'YEARLY',
//       amount: parseFloat(amount.replace(/,/g, '')),
//       includeCredits: provider === 'LANGFUSE' ? false : includeCredits,
//       metric: provider === 'LANGFUSE' ? metric : null,
//       alertThresholds: thresholds.map(t => parseInt(t.percentage, 10)).filter(t => !isNaN(t)),
//       userIdsToNotify: alertRecipients.map(recipient => {
//         if (recipient.type === 'role') {
//           return `ROLE:${recipient.data.name.toLowerCase()}`;
//         }
//         return recipient.data.id as string;
//       }),
//       scopeConfigurations: services.map(service => ({
//         dataSourceIds: service.dataSources,
//         projectIds: provider === 'GCP' ? service.projectIds : null,
//         regions: provider === 'GCP' ? service.regions : null,
//         services: service.serviceTypes,
//         labels: provider === 'GCP' ? service.label.flatMap((l: LabelFilterPair) =>
//             l.values.map((v: string) => ({ key: l.key, value: v }))
//         ) : null,
//         k8sClusters: provider === 'GCP' ? service.cluster : null,
//         k8sNamespaces: provider === 'GCP' ? service.namespace : null,
//         skus: provider === 'LANGFUSE' ? service.skus : null,
//       })),
//     };

//     try {
//       const result = await updateBudget({
//         variables: { id: budgetId, input: updateBudgetInput },
//       });

//       if (result.data?.updateBudget) {
//         navigate('/budget-alerts', {
//           replace: true,
//           state: { budgetUpdated: true, budgetName: result.data.updateBudget.name }
//         });
//       } else {
//         throw new Error("Received no data from server after update.");
//       }
//     } catch (err: any) {
//       console.error("Failed to update budget:", err);
//       setToastMessage(`Error: ${err.message || 'Could not save changes.'}`);
//       setIsToastOpen(true);
//     }
//   };

//   const handleAddTag = () => {
//     const newTag: Tag = { id: Date.now(), label: dialogLabel, value: dialogTag };
//     if (newTag.label && newTag.value && !tags.some(tag => tag.label === newTag.label && tag.value === newTag.value)) {
//       setTags(prevTags => [...prevTags, newTag]);
//     }
//     setIsDialogOpen(false);
//     setDialogLabel('');
//     setDialogTag('');
//   };

//   const handleDeleteTag = (tagToDelete: Tag) => {
//     setLastDeletedTag(tagToDelete);
//     setTags(prevTags => prevTags.filter(tag => tag.id !== tagToDelete.id));
//     setToastMessage("Label deleted successfully");
//     setIsToastOpen(true);
//   };

//   const handleUndo = () => {
//     if (lastDeletedTag) {
//       setTags(prevTags => [...prevTags, lastDeletedTag]);
//       setLastDeletedTag(null);
//       setIsToastOpen(false);
//     }
//   };

//   const handleToastClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
//     if (reason === 'clickaway') return;
//     setIsToastOpen(false);
//     setLastDeletedTag(null);
//   };

//   const handleCloseDialog = () => {
//     setIsDialogOpen(false);
//     setDialogLabel('');
//     setDialogTag('');
//   };

//   if (queryLoading) {
//     return (
//       <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
//         <CircularProgress />
//       </Box>
//     );
//   }

//   if (queryError) {
//     return (
//         <Box sx={{ p: 3, textAlign: 'center' }}>
//             <Typography color="error" variant="h6" gutterBottom>Error Loading Budget</Typography>
//             <Typography color="error">Could not fetch budget details. Please try again later.</Typography>
//             <pre style={{ whiteSpace: 'pre-wrap', textAlign: 'left', backgroundColor: '#fdecea', border: '1px solid #f44336', padding: '1rem', marginTop: '1rem', borderRadius: '8px' }}>
//                 {queryError.message}
//             </pre>
//             <Button sx={{mt: 2}} variant="contained" onClick={() => navigate('/budget-alerts')}>Go Back</Button>
//         </Box>
//     );
//   }

//   return (
//     <Box sx={{ p: { xs: 1, sm: 0 }, height: '100%', boxSizing: 'border-box', backgroundColor: '#F8F9FA' }}>
//       <LabelSuccessToast
//         open={isToastOpen}
//         onClose={handleToastClose}
//         message={toastMessage}
//         onUndo={lastDeletedTag ? handleUndo : () => {}}
//       />
      
//       <Box sx={{ mb: 2 }}>
//         <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: -1 }}>
//           <IconButton onClick={() => navigate('/budget-alerts')} size="small" sx={{ color: '#333333', ml: -1 }}>
//             <ArrowBackIcon sx={{ width: '20px', height: '20px' }} />
//           </IconButton>
//           <Typography variant="body2" color="#333333" sx={{ fontWeight: 600, fontFamily: 'Inter', fontSize: '14px' }}>
//             Edit Budget
//           </Typography>
//         </Box>
//         <Typography variant="body2" color="#666666" sx={{ fontWeight: 400, fontFamily: 'Inter', fontSize: '12px', ml: 2.5, mt: -1 }}>
//           Edit the below details to update the budget
//         </Typography>
//       </Box>
      
//       <Box component="div" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
//         <BasicDetails
//           budgetName={budgetName}
//           onBudgetNameChange={handleBudgetNameChange}
//           budgetDescription={budgetDescription}
//           onBudgetDescriptionChange={handleBudgetDescriptionChange}
//           tags={tags}
//           handleDeleteTag={handleDeleteTag}
//           setIsDialogOpen={setIsDialogOpen}
//           errors={errors}
//         />
//         {/* Pass new props to BudgetDetails */}
//         <BudgetDetails
//           period={period}
//           setPeriod={setPeriod}
//           currency={currency}
//           setCurrency={setCurrency}
//           amount={amount}
//           setAmount={setAmount}
//           includeCredits={includeCredits}
//           setIncludeCredits={setIncludeCredits}
//           thresholds={thresholds}
//           setThresholds={setThresholds}
//           errors={errors}
//           provider={provider}
//           metric={metric}
//           setMetric={setMetric}
//         />
//         {/* Pass isEditMode to ScopeConfig to disable provider selection */}
//         <ScopeConfig
//           provider={provider}
//           setProvider={setProvider}
//           services={services}
//           setServices={setServices}
//           getInitialService={getInitialService}
//           errors={errors}
//           disabled={!isBudgetDetailsComplete}
//           period={period}
          
//         />
//         <AlertSettings
//           recipients={alertRecipients}
//           setRecipients={setAlertRecipients}
//           errors={errors}
//           roles={staticRoles}
//           allUsers={allUsers}
//           isLoading={usersLoading || queryLoading}
//         />
//         <Box sx={{ p: 2, bgcolor: '#FFFFFF', borderRadius: '10px', border: '1px solid #F3F3F3', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
//           <Box sx={{ display: 'flex', gap: 1 }}>
//             <Button onClick={() => navigate('/budget-alerts')} sx={{ width: '79px', height: '36px', borderRadius: '8px', backgroundColor: '#F2F7FF', color: '#0064FF', fontFamily: 'Inter', fontWeight: 500, fontSize: '14px', textTransform: 'none', '&:hover': { backgroundColor: '#E0EFFF' } }}>
//               Cancel
//             </Button>
//             <Button
//               variant="contained"
//               onClick={handleSaveChanges}
//               disabled={mutationLoading}
//               sx={{ width: '130px', height: '36px', borderRadius: '8px', backgroundColor: '#0064FF', color: 'white', fontFamily: 'Inter', fontWeight: 500, fontSize: '14px', textTransform: 'none', '&:hover': { backgroundColor: '#0053D9' } }}
//             >
//               {mutationLoading ? <CircularProgress size={24} color="inherit" /> : 'Save Changes'}
//             </Button>
//           </Box>
//         </Box>
//       </Box>
//       <Dialog
//         open={isDialogOpen}
//         onClose={handleCloseDialog}
//         PaperProps={{
//           ref: paperRef,
//           sx: {
//             borderRadius: '12px', width: '600px', height: '322px', maxWidth: '600px', overflow: 'hidden',
//             backgroundImage: `url('/textures/Texture.svg'), url('/textures/Gradient_Popup.svg')`,
//             backgroundRepeat: 'no-repeat, no-repeat', backgroundPosition: 'top right, center', backgroundSize: 'auto, cover',
//           }
//         }}
//       >
//        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
//           <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
//             <img src="/icons/add-filter-icon1.svg" alt="Add" style={{ width: '56px', height: '56px', marginRight: '16px' }} />
//             <Box>
//               <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '20px', color: '#333333', fontFamily: 'Inter' }}>Add Label/ Tag</Typography>
//               <Typography variant="body2" sx={{ fontWeight: 400,fontSize: '14px', color: '#666666', fontFamily: 'Inter' }}>Add label details to setup alerts.</Typography>
//             </Box>
//           </Box>
//           <DialogContent sx={{ p: '0 !important', mt: 7, mb: 3, flexGrow: 1 }}>
//             <Box sx={{ display: 'flex', gap: 2 }}>
//               <Box sx={{ flex: 1 }}>
//                 <Typography variant="body2" sx={{ mb: 1, fontFamily: 'Inter', fontSize: '14px',fontWeight: 400 }}>Key<span style={{color: 'red'}}>*</span></Typography>
//                 <TextField fullWidth variant="outlined" placeholder="Enter label" value={dialogLabel} onChange={(e) => setDialogLabel(e.target.value)} size="small" InputProps={{ endAdornment: ( <InputAdornment position="end"> {dialogLabel && ( <CheckIcon sx={{ color: '#03A65A', fontSize: '15px' }} /> )} </InputAdornment> ) }} sx={{ '& .MuiOutlinedInput-root': { height: '40px', borderRadius: '8px', fontFamily: 'Inter', backgroundColor: '#FFFFFF', '& .MuiInputBase-input:-webkit-autofill': { WebkitBoxShadow: '0 0 0 100px #FFFFFF inset' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#E0E0E0', }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0064FF',borderWidth: '1px' } }, '& .MuiInputBase-input::placeholder': { fontSize: '14px', fontFamily: 'Inter', }, '& .MuiInputBase-input': { fontFamily: 'Inter', fontSize: '14px', }, }}/>
//               </Box>
//               <Box sx={{ flex: 1 }}>
//                 <Typography variant="body2" sx={{ mb: 1, fontFamily: 'Inter', fontSize: '14px',fontWeight: 400 }}>Value<span style={{color: 'red'}}>*</span></Typography>
//                 <TextField fullWidth variant="outlined" placeholder="Enter tag" value={dialogTag} onChange={(e) => setDialogTag(e.target.value)} size="small" InputProps={{ endAdornment: ( <InputAdornment position="end"> {dialogTag && ( <CheckIcon sx={{ color: '#03A65A', fontSize: '15px' }} /> )} </InputAdornment> ) }} sx={{ '& .MuiOutlinedInput-root': { height: '40px', borderRadius: '8px', fontFamily: 'Inter', backgroundColor: '#FFFFFF', '& .MuiInputBase-input:-webkit-autofill': { WebkitBoxShadow: '0 0 0 100px #FFFFFF inset' }, '& fieldset': { borderColor: '#E0E0E0', }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#E0E0E0' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0064FF',borderWidth: '1px' } }, '& .MuiInputBase-input::placeholder': { fontSize: '14px', fontFamily: 'Inter', }, '& .MuiInputBase-input': { fontFamily: 'Inter', fontSize: '14px',}, }}/>
//               </Box>
//             </Box>
//           </DialogContent>
//           <DialogActions sx={{ p: 0 }}>
//             <Button onClick={handleCloseDialog} sx={{ width:"79px", height:"36px",textTransform: 'none', borderRadius: '8px', color: '#0064FF', backgroundColor: '#F2F7FF', fontFamily: 'Inter', fontSize:"14px", fontWeight:500 }}>Cancel</Button>
//             <Button variant="contained" onClick={handleAddTag} disabled={!dialogLabel || !dialogTag} sx={{ width:"74px", height:"36px",textTransform: 'none', borderRadius: '8px', backgroundColor: '#0064FF', fontFamily: 'Inter', fontSize:"14px", fontWeight:500 }}> Add </Button>
//           </DialogActions>
//         </Box>
//       </Dialog>
//     </Box>
//   );
// };

// export default EditBudgetPage;




// // import React, { useState, useEffect, useRef } from 'react';
// // import {
// //   Box,
// //   Typography,
// //   Button,
// //   IconButton,
// //   TextField,
// //   Dialog,
// //   DialogActions,
// //   DialogContent,
// //   InputAdornment,
// //   CircularProgress,
// // } from '@mui/material';
// // import { useNavigate, useParams, useOutletContext } from 'react-router-dom';
// // import { gql, useQuery, useMutation, useApolloClient } from '@apollo/client';
// // import ArrowBackIcon from '@mui/icons-material/ArrowBack';
// // import CheckIcon from '@mui/icons-material/Check';
// // import BasicDetails from './BasicDetails';
// // import BudgetDetails from './BudgetDetails';
// // import ScopeConfig from './ScopeConfig';
// // import AlertSettings from './AlertSettings';
// // import LabelSuccessToast from './LabelSuccessToast';
// // import { LabelFilterPair } from './LabelFilterMenu';
// // // import { Tag } from '../../types'; // <-- IMPORT THE SHARED TYPE

// // // --- TYPE DEFINITIONS ---
// // interface UserData {
// //   id: string;
// //   name: string;
// //   email: string;
// //   role: string;
// //   avatar?: string;
// // }
// // interface RoleData {
// //   name: string;
// //   description: string;
// //   icon: string;
// // }
// // type AlertRecipient = {
// //   data: { id?: string; name: string; [key: string]: any };
// //   type: 'role' | 'user';
// // };

// // interface Tag {
// //   label: string;
// //   value: string;
// //   id: string | number;
// // }

// // interface RoleData {
// //   name: string;
// //   description: string;
// //   icon: string;
// //   color: string;
// // }

// // // --- REMOVED LOCAL TAG INTERFACE ---
// // /*
// // interface Tag {
// //   label: string;
// //   value: string;
// //   id: string | number;
// // }
// // */

// // interface AppOutletContext {
// //   currentTenant?: { id: string; name: string; };
// // }
// // export interface ServiceState {
// //   id: string;
// //   dataSources: string[];
// //   regions: string[];
// //   serviceTypes: string[];
// //   label: LabelFilterPair[];
// //   cluster: string[];
// //   namespace: string[];
// //   deployment: string[];
// // }

// // // Static roles list is needed to map saved 'ROLE:name' strings back to full objects

// // // Replace with this:
// // const staticRoles: RoleData[] = [
// //   { name: 'Admin', description: 'All current/ new users with admin role', icon: '/icons/admin.svg', color: '#FF8900' },
// //   { name: 'Editor', description: 'All current/ new users with editor role', icon: '/icons/editor.svg', color: '#49A7E4' },
// //   { name: 'Viewer', description: 'All current/ new users with viewer role', icon: '/icons/viewer.svg', color: '#666666' },
// // ];


// // // --- QUERIES AND MUTATIONS ---
// // const GET_BUDGET_BY_ID = gql`
// //   query GetBudgetById($id: ID!) {
// //     budgetById(id: $id) {
// //       id
// //       name
// //       description
// //       tags
// //       provider
// //       period
// //       amount
// //       includeCredits
// //       alertThresholds
// //       userIdsToNotify
// //       scopeConfigurations {
// //         id
// //         dataSources { id }
// //         regions
// //         services
// //         labels
// //         k8sClusters
// //         k8sNamespaces
// //       }
// //     }
// //   }
// // `;

// // const GET_USERS_BY_ROLE = gql`
// //   query GetUsersByRoleInTenant($tenantId: ID!, $roles: [Role!]!) {
// //     getUsersByRoleInTenant(tenantId: $tenantId, roles: $roles) {
// //       id
// //       name
// //       email
// //       role
// //       photo
// //     }
// //   }
// // `;

// // const UPDATE_BUDGET_MUTATION = gql`
// //   mutation UpdateBudget($id: ID!, $input: UpdateBudgetInput!) {
// //     updateBudget(id: $id, input: $input) {
// //       id
// //       name
// //     }
// //   }
// // `;

// // const getInitialService = (): Omit<ServiceState, 'id' | 'serviceTypes'> & { serviceTypes: string[] } => ({
// //   dataSources: [],
// //   regions: [],
// //   serviceTypes: [],
// //   label: [],
// //   cluster: [],
// //   namespace: [],
// //   deployment: []
// // });

// // const EditBudgetPage: React.FC = () => {
// //   const navigate = useNavigate();
// //   const { budgetId } = useParams<{ budgetId: string }>();
// //   const { currentTenant } = useOutletContext<AppOutletContext>();
// //   const paperRef = useRef(null);
// //   const client = useApolloClient();

// //   // --- STATE MANAGEMENT ---
// //   const [errors, setErrors] = useState<{ [key: string]: string }>({});
// //   const [budgetName, setBudgetName] = useState('');
// //   const [budgetDescription, setBudgetDescription] = useState('');
// //   const [tags, setTags] = useState<Tag[]>([]);
// //   const [provider, setProvider] = useState('');
// //   const [services, setServices] = useState<ServiceState[]>([]);
// //   const [period, setPeriod] = useState<'Monthly' | 'Quarterly' | 'Yearly'>('Monthly');
// //   const [currency, setCurrency] = useState<'INR' | 'USD'>('USD');
// //   const [amount, setAmount] = useState('');
// //   const [includeCredits, setIncludeCredits] = useState(false);
// //   const [thresholds, setThresholds] = useState<{ id: number; percentage: string; }[]>([]);
// //   const [alertRecipients, setAlertRecipients] = useState<AlertRecipient[]>([]);
// //   const [isDialogOpen, setIsDialogOpen] = useState(false);
// //   const [dialogLabel, setDialogLabel] = useState('');
// //   const [dialogTag, setDialogTag] = useState('');
// //   const [isToastOpen, setIsToastOpen] = useState(false);
// //   const [lastDeletedTag, setLastDeletedTag] = useState<Tag | null>(null);
// //   const [toastMessage, setToastMessage] = useState('');
// //   const [allUsers, setAllUsers] = useState<UserData[]>([]);
// //   const [usersLoading, setUsersLoading] = useState(true);

// //   // --- DATA FETCHING & PROCESSING ---

// //   useEffect(() => {
// //     if (!currentTenant?.id) return;
// //     const fetchAllUsers = async () => {
// //       setUsersLoading(true);
// //       const rolesToFetch = ['admin', 'editor', 'viewer'];
// //       try {
// //         const promises = rolesToFetch.map(role =>
// //           client.query({
// //             query: GET_USERS_BY_ROLE,
// //             variables: { tenantId: currentTenant.id, roles: [role] }
// //           })
// //         );
// //         const results = await Promise.all(promises);
// //         const combinedUsers = results.flatMap(result => result.data.getUsersByRoleInTenant);
// //         const uniqueUsers = Array.from(new Map(combinedUsers.map(user => [user.id, {
// //     ...user,
// //     avatar: user.photo // This line maps the photo to the avatar
// // }])).values());
// //         // const uniqueUsers = Array.from(new Map(combinedUsers.map(user => [user.id, user])).values());
// //         setAllUsers(uniqueUsers);
// //       } catch (error) {
// //         console.error("Failed to fetch users for dropdown:", error);
// //         setAllUsers([]);
// //       } finally {
// //         setUsersLoading(false);
// //       }
// //     };
// //     fetchAllUsers();
// //   }, [currentTenant?.id, client]);

// //   const { data: budgetData, loading: queryLoading, error: queryError } = useQuery(GET_BUDGET_BY_ID, {
// //     variables: { id: budgetId },
// //     skip: !budgetId,
// //     onCompleted: (data) => {
// //       if (data && data.budgetById) {
// //         const budgetToEdit = data.budgetById;
// //         setBudgetName(budgetToEdit.name || '');
// //         setBudgetDescription(budgetToEdit.description || '');
// //         const tagsArray = budgetToEdit.tags
// //           ? Object.entries(budgetToEdit.tags).map(([label, value], index): Tag => ({
// //               id: `loaded_${label}_${index}`,
// //               label,
// //               value: String(value),
// //             }))
// //           : [];
// //         setTags(tagsArray);
// //         setProvider(budgetToEdit.provider || '');
        
// //         const mappedServices = budgetToEdit.scopeConfigurations.map((sc: any, index: number) => {
// //             const groupedLabels = (sc.labels || []).reduce((acc: {[key: string]: string[]}, label: {key: string, value: string}) => {
// //                 if (!acc[label.key]) acc[label.key] = [];
// //                 acc[label.key].push(label.value);
// //                 return acc;
// //             }, {});

// //             const serviceLabels = Object.entries(groupedLabels).map(([key, values], idx) => ({
// //                 id: `${key}_${idx}`, key, values, source: 'existing'
// //             }));

// //             return {
// //                 id: sc.id || `service_${Date.now() + index}`,
// //                 dataSources: sc.dataSources?.map((ds: { id: string }) => ds.id) || [],
// //                 regions: sc.regions || [],
// //                 serviceTypes: sc.services || [],
// //                 label: serviceLabels,
// //                 cluster: sc.k8sClusters || [],
// //                 namespace: sc.k8sNamespaces || [],
// //                 deployment: []
// //             };
// //         });

// //         setServices(mappedServices);
// //         setPeriod(budgetToEdit.period?.charAt(0).toUpperCase() + budgetToEdit.period?.slice(1).toLowerCase() as 'Monthly' | 'Quarterly' | 'Yearly' || 'Monthly');
// //         setAmount(budgetToEdit.amount?.toString() || '');
// //         setIncludeCredits(budgetToEdit.includeCredits || false);
// //         setThresholds(budgetToEdit.alertThresholds?.map((p: any, i: number) => ({ id: Date.now() + i, percentage: p.toString() })) || []);
// //       } else {
// //         navigate('/budget-alerts');
// //       }
// //     }
// //   });

// //   useEffect(() => {
// //     const savedRecipientIds = budgetData?.budgetById?.userIdsToNotify;
// //     if (savedRecipientIds && allUsers.length > 0) {
// //       const initialRecipients = savedRecipientIds.map((idOrRole: string) => {
// //         if (idOrRole.startsWith('ROLE:')) {
// //           const roleName = idOrRole.split(':')[1];
// //           const roleData = staticRoles.find(
// //             (r) => r.name.toLowerCase() === roleName.toLowerCase()
// //           );
// //           return roleData ? { type: 'role' as const, data: roleData } : null;
// //         } else {
// //           const userData = allUsers.find(u => u.id === idOrRole);
// //           return userData ? { type: 'user' as const, data: userData } : null;
// //         }
// //       }).filter((item: AlertRecipient | null): item is AlertRecipient => item !== null);
      
// //       setAlertRecipients(initialRecipients);
// //     }
// //   }, [budgetData, allUsers]);

// //   const [updateBudget, { loading: mutationLoading }] = useMutation(UPDATE_BUDGET_MUTATION);

// //   // --- HANDLER FUNCTIONS ---
// //   const isBudgetDetailsComplete = period && amount.trim() !== '';

// //   useEffect(() => {
// //     const newErrors = { ...errors };
// //     let hasErrorChanged = false;

// //     if (amount.trim() && newErrors.amount) {
// //         delete newErrors.amount;
// //         hasErrorChanged = true;
// //     }
// //     if (provider && newErrors.provider) {
// //         delete newErrors.provider;
// //         hasErrorChanged = true;
// //     }
// //     if (services.length > 0 && (newErrors.dataSources || newErrors.regions || newErrors.services)) {
// //         delete newErrors.dataSources;
// //         delete newErrors.regions;
// //         delete newErrors.services;
// //         hasErrorChanged = true;
// //     }
// //     if (alertRecipients.length > 0 && newErrors.alertRecipients) {
// //         delete newErrors.alertRecipients;
// //         hasErrorChanged = true;
// //     }
// //     //  if (tags.length > 0 && newErrors.tags) {
// //     //     delete newErrors.tags;
// //     //     hasErrorChanged = true;
// //     // }

// //     if (hasErrorChanged) {
// //         setErrors(newErrors);
// //     }
// //   }, [amount, provider, services, alertRecipients, tags, errors]);

// //   const handleBudgetNameChange = (name: string) => {
// //     setBudgetName(name);
// //     const newErrors = { ...errors };

// //     if (newErrors.budgetName) {
// //         delete newErrors.budgetName;
// //     }

// //     if (!/^[a-zA-Z0-9\s_-]*$/.test(name)) {
// //       newErrors.budgetName = 'Name can only contain letters, numbers, spaces, -, and _.';
// //     }
// //     setErrors(newErrors);
// //   };

// //   const handleBudgetDescriptionChange = (description: string) => {
// //     setBudgetDescription(description);
// //     const newErrors = { ...errors };

// //     if(newErrors.budgetDescription) {
// //         delete newErrors.budgetDescription;
// //     }

// //     if (description.length > 200) {
// //       newErrors.budgetDescription = 'Description cannot exceed 200 characters.';
// //     }
// //     setErrors(newErrors);
// //   };

// //   const validateForm = () => {
// //     const newErrors: { [key: string]: string } = {};

// //     const trimmedName = budgetName.trim();
// //     if (!trimmedName) {
// //       newErrors.budgetName = 'Budget Name is required.';
// //     } else if (trimmedName.length < 3) {
// //       newErrors.budgetName = 'Name must be at least 3 characters.';
// //     } else if (trimmedName.length > 50) {
// //       newErrors.budgetName = 'Name cannot exceed 50 characters.';
// //     } else if (!/^[a-zA-Z0-9\s_-]+$/.test(trimmedName)) {
// //       newErrors.budgetName = 'Name can only contain letters, numbers, spaces, -, and _.';
// //     }

// //     const trimmedDescription = budgetDescription.trim();
// //     if (!trimmedDescription) {
// //       newErrors.budgetDescription = 'Budget Description is required.';
// //     } else if (trimmedDescription.length > 200) {
// //       newErrors.budgetDescription = 'Description cannot exceed 200 characters.';
// //     }

// //     // if (tags.length === 0) newErrors.tags = 'At least one Label/Tag is required.';
// //     if (!provider) newErrors.provider = 'A Cloud Provider must be selected.';
// //     if (services.length === 0 || !services.every(s => s.dataSources.length > 0)) newErrors.dataSources = 'Each service must have a Data Source.';
// //     if (services.length === 0 || !services.every(s => s.regions.length > 0)) newErrors.regions = 'Each service must have a Region.';
// //     if (services.length === 0 || !services.every(s => s.serviceTypes.length > 0)) newErrors.services = 'Each service must have a Service Type configured.';
// //     if (!amount.trim()) newErrors.amount = 'Budget Amount is required.';
// //     if (alertRecipients.length === 0) newErrors.alertRecipients = 'At least one Alert Recipient is required.';

// //     setErrors(newErrors);
// //     return Object.keys(newErrors).length === 0;
// //   };

// //   const handleSaveChanges = async () => {
// //     if (!validateForm() || !budgetId) return;

// //     const updateBudgetInput = {
// //       name: budgetName,
// //       description: budgetDescription,
// //       tags: tags.reduce((obj, item) => ({ ...obj, [item.label]: item.value }), {}),
// //       period: period.toUpperCase() as 'MONTHLY' | 'QUARTERLY' | 'YEARLY',
// //       amount: parseFloat(amount.replace(/,/g, '')),
// //       includeCredits: includeCredits,
// //       alertThresholds: thresholds.map(t => parseInt(t.percentage, 10)).filter(t => !isNaN(t)),
// //       userIdsToNotify: alertRecipients.map(recipient => {
// //         if (recipient.type === 'role') {
// //           return `ROLE:${recipient.data.name.toLowerCase()}`;
// //         }
// //         return recipient.data.id as string;
// //       }),
// //       scopeConfigurations: services.map(service => ({
// //         dataSourceIds: service.dataSources,
// //         regions: service.regions,
// //         services: service.serviceTypes,
// //         labels: service.label.flatMap((l: LabelFilterPair) =>
// //             l.values.map((v: string) => ({ key: l.key, value: v }))
// //         ),
// //         k8sClusters: service.cluster,
// //         k8sNamespaces: service.namespace,
// //       })),
// //     };

// //     try {
// //       const result = await updateBudget({
// //         variables: { id: budgetId, input: updateBudgetInput },
// //       });

// //       if (result.data?.updateBudget) {
// //         navigate('/budget-alerts', {
// //           replace: true,
// //           state: { budgetUpdated: true, budgetName: result.data.updateBudget.name }
// //         });
// //       } else {
// //         throw new Error("Received no data from server after update.");
// //       }
// //     } catch (err: any) {
// //       console.error("Failed to update budget:", err);
// //       setToastMessage(`Error: ${err.message || 'Could not save changes.'}`);
// //       setIsToastOpen(true);
// //     }
// //   };

// //   const handleAddTag = () => {
// //     const newTag: Tag = { id: Date.now(), label: dialogLabel, value: dialogTag };
// //     if (newTag.label && newTag.value && !tags.some(tag => tag.label === newTag.label && tag.value === newTag.value)) {
// //       setTags(prevTags => [...prevTags, newTag]);
// //     }
// //     setIsDialogOpen(false);
// //     setDialogLabel('');
// //     setDialogTag('');
// //   };

// //   const handleDeleteTag = (tagToDelete: Tag) => {
// //     setLastDeletedTag(tagToDelete);
// //     setTags(prevTags => prevTags.filter(tag => tag.id !== tagToDelete.id));
// //     setToastMessage("Label deleted successfully");
// //     setIsToastOpen(true);
// //   };

// //   const handleUndo = () => {
// //     if (lastDeletedTag) {
// //       setTags(prevTags => [...prevTags, lastDeletedTag]);
// //       setLastDeletedTag(null);
// //       setIsToastOpen(false);
// //     }
// //   };

// //   const handleToastClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
// //     if (reason === 'clickaway') return;
// //     setIsToastOpen(false);
// //     setLastDeletedTag(null);
// //   };

// //   const handleCloseDialog = () => {
// //     setIsDialogOpen(false);
// //     setDialogLabel('');
// //     setDialogTag('');
// //   };

// //   // --- RENDER LOGIC ---
// //   if (queryLoading) {
// //     return (
// //       <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
// //         <CircularProgress />
// //       </Box>
// //     );
// //   }

// //   if (queryError) {
// //     return (
// //         <Box sx={{ p: 3, textAlign: 'center' }}>
// //             <Typography color="error" variant="h6" gutterBottom>Error Loading Budget</Typography>
// //             <Typography color="error">Could not fetch budget details. Please try again later.</Typography>
// //             <pre style={{ whiteSpace: 'pre-wrap', textAlign: 'left', backgroundColor: '#fdecea', border: '1px solid #f44336', padding: '1rem', marginTop: '1rem', borderRadius: '8px' }}>
// //                 {queryError.message}
// //             </pre>
// //             <Button sx={{mt: 2}} variant="contained" onClick={() => navigate('/budget-alerts')}>Go Back</Button>
// //         </Box>
// //     );
// //   }

// //   return (
// //     <Box sx={{ p: { xs: 1, sm: 0 }, height: '100%', boxSizing: 'border-box', backgroundColor: '#F8F9FA' }}>
// //       <LabelSuccessToast
// //         open={isToastOpen}
// //         onClose={handleToastClose}
// //         message={toastMessage}
// //         onUndo={lastDeletedTag ? handleUndo : () => {}}
// //       />
      
// //       <Box sx={{ mb: 2 }}>
// //         <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: -1 }}>
// //           <IconButton onClick={() => navigate('/budget-alerts')} size="small" sx={{ color: '#333333', ml: -1 }}>
// //             <ArrowBackIcon sx={{ width: '20px', height: '20px' }} />
// //           </IconButton>
// //           <Typography variant="body2" color="#333333" sx={{ fontWeight: 600, fontFamily: 'Inter', fontSize: '14px' }}>
// //             Edit Budget
// //           </Typography>
// //         </Box>
// //         <Typography variant="body2" color="#666666" sx={{ fontWeight: 400, fontFamily: 'Inter', fontSize: '12px', ml: 2.5, mt: -1 }}>
// //           Edit the below details to update the budget
// //         </Typography>
// //       </Box>
      
// //       <Box component="div" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
// //         <BasicDetails
// //           budgetName={budgetName}
// //           onBudgetNameChange={handleBudgetNameChange}
// //           budgetDescription={budgetDescription}
// //           onBudgetDescriptionChange={handleBudgetDescriptionChange}
// //           tags={tags}
// //           handleDeleteTag={handleDeleteTag}
// //           setIsDialogOpen={setIsDialogOpen}
// //           errors={errors}
// //         />
// //         <BudgetDetails
// //           period={period}
// //           setPeriod={setPeriod}
// //           currency={currency}
// //           setCurrency={setCurrency}
// //           amount={amount}
// //           setAmount={setAmount}
// //           includeCredits={includeCredits}
// //           setIncludeCredits={setIncludeCredits}
// //           thresholds={thresholds}
// //           setThresholds={setThresholds}
// //           errors={errors}
// //         />
// //         <ScopeConfig
// //           provider={provider}
// //           setProvider={setProvider}
// //           services={services}
// //           setServices={setServices}
// //           getInitialService={getInitialService}
// //           errors={errors}
// //           disabled={!isBudgetDetailsComplete}
// //           period={period}
// //         />
// //         <AlertSettings
// //           recipients={alertRecipients}
// //           setRecipients={setAlertRecipients}
// //           errors={errors}
// //           roles={staticRoles}
// //           allUsers={allUsers}
// //           isLoading={usersLoading || queryLoading}
// //         />
// //         <Box sx={{ p: 2, bgcolor: '#FFFFFF', borderRadius: '10px', border: '1px solid #F3F3F3', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
// //           <Box sx={{ display: 'flex', gap: 1 }}>
// //             <Button onClick={() => navigate('/budget-alerts')} sx={{ width: '79px', height: '36px', borderRadius: '8px', backgroundColor: '#F2F7FF', color: '#0064FF', fontFamily: 'Inter', fontWeight: 500, fontSize: '14px', textTransform: 'none', '&:hover': { backgroundColor: '#E0EFFF' } }}>
// //               Cancel
// //             </Button>
// //             <Button
// //               variant="contained"
// //               onClick={handleSaveChanges}
// //               disabled={mutationLoading}
// //               sx={{ width: '130px', height: '36px', borderRadius: '8px', backgroundColor: '#0064FF', color: 'white', fontFamily: 'Inter', fontWeight: 500, fontSize: '14px', textTransform: 'none', '&:hover': { backgroundColor: '#0053D9' } }}
// //             >
// //               {mutationLoading ? <CircularProgress size={24} color="inherit" /> : 'Save Changes'}
// //             </Button>
// //           </Box>
// //         </Box>
// //       </Box>
// //       <Dialog
// //         open={isDialogOpen}
// //         onClose={handleCloseDialog}
// //         PaperProps={{
// //           ref: paperRef,
// //           sx: {
// //             borderRadius: '12px', width: '600px', height: '322px', maxWidth: '600px', overflow: 'hidden',
// //             backgroundImage: `url('/textures/Texture.svg'), url('/textures/Gradient_Popup.svg')`,
// //             backgroundRepeat: 'no-repeat, no-repeat', backgroundPosition: 'top right, center', backgroundSize: 'auto, cover',
// //           }
// //         }}
// //       >
// //        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
// //           <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
// //             <img src="/icons/add-filter-icon1.svg" alt="Add" style={{ width: '56px', height: '56px', marginRight: '16px' }} />
// //             <Box>
// //               <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '20px', color: '#333333', fontFamily: 'Inter' }}>Add Label/ Tag</Typography>
// //               <Typography variant="body2" sx={{ fontWeight: 400,fontSize: '14px', color: '#666666', fontFamily: 'Inter' }}>Add label details to setup alerts.</Typography>
// //             </Box>
// //           </Box>
// //           <DialogContent sx={{ p: '0 !important', mt: 7, mb: 3, flexGrow: 1 }}>
// //             <Box sx={{ display: 'flex', gap: 2 }}>
// //               <Box sx={{ flex: 1 }}>
// //                 <Typography variant="body2" sx={{ mb: 1, fontFamily: 'Inter', fontSize: '14px',fontWeight: 400 }}>Key<span style={{color: 'red'}}>*</span></Typography>
// //                 <TextField fullWidth variant="outlined" placeholder="Enter label" value={dialogLabel} onChange={(e) => setDialogLabel(e.target.value)} size="small" InputProps={{ endAdornment: ( <InputAdornment position="end"> {dialogLabel && ( <CheckIcon sx={{ color: '#03A65A', fontSize: '15px' }} /> )} </InputAdornment> ) }} sx={{ '& .MuiOutlinedInput-root': { height: '40px', borderRadius: '8px', fontFamily: 'Inter', backgroundColor: '#FFFFFF', '& .MuiInputBase-input:-webkit-autofill': { WebkitBoxShadow: '0 0 0 100px #FFFFFF inset' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#E0E0E0', }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0064FF',borderWidth: '1px' } }, '& .MuiInputBase-input::placeholder': { fontSize: '14px', fontFamily: 'Inter', }, '& .MuiInputBase-input': { fontFamily: 'Inter', fontSize: '14px', }, }}/>
// //               </Box>
// //               <Box sx={{ flex: 1 }}>
// //                 <Typography variant="body2" sx={{ mb: 1, fontFamily: 'Inter', fontSize: '14px',fontWeight: 400 }}>Value<span style={{color: 'red'}}>*</span></Typography>
// //                 <TextField fullWidth variant="outlined" placeholder="Enter tag" value={dialogTag} onChange={(e) => setDialogTag(e.target.value)} size="small" InputProps={{ endAdornment: ( <InputAdornment position="end"> {dialogTag && ( <CheckIcon sx={{ color: '#03A65A', fontSize: '15px' }} /> )} </InputAdornment> ) }} sx={{ '& .MuiOutlinedInput-root': { height: '40px', borderRadius: '8px', fontFamily: 'Inter', backgroundColor: '#FFFFFF', '& .MuiInputBase-input:-webkit-autofill': { WebkitBoxShadow: '0 0 0 100px #FFFFFF inset' }, '& fieldset': { borderColor: '#E0E0E0', }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#E0E0E0' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0064FF',borderWidth: '1px' } }, '& .MuiInputBase-input::placeholder': { fontSize: '14px', fontFamily: 'Inter', }, '& .MuiInputBase-input': { fontFamily: 'Inter', fontSize: '14px',}, }}/>
// //               </Box>
// //             </Box>
// //           </DialogContent>
// //           <DialogActions sx={{ p: 0 }}>
// //             <Button onClick={handleCloseDialog} sx={{ width:"79px", height:"36px",textTransform: 'none', borderRadius: '8px', color: '#0064FF', backgroundColor: '#F2F7FF', fontFamily: 'Inter', fontSize:"14px", fontWeight:500 }}>Cancel</Button>
// //             <Button variant="contained" onClick={handleAddTag} disabled={!dialogLabel || !dialogTag} sx={{ width:"74px", height:"36px",textTransform: 'none', borderRadius: '8px', backgroundColor: '#0064FF', fontFamily: 'Inter', fontSize:"14px", fontWeight:500 }}> Add </Button>
// //           </DialogActions>
// //         </Box>
// //       </Dialog>
// //     </Box>
// //   );
// // };

// // export default EditBudgetPage;


// import React, { useState, useEffect, useRef } from 'react';
// import {
//   Box,
//   Typography,
//   Button,
//   IconButton,
//   TextField,
//   Dialog,
//   DialogActions,
//   DialogContent,
//   InputAdornment,
//   CircularProgress,
// } from '@mui/material';
// import { useNavigate, useParams, useOutletContext } from 'react-router-dom';
// import { gql, useQuery, useMutation, useApolloClient } from '@apollo/client';
// import ArrowBackIcon from '@mui/icons-material/ArrowBack';
// import CheckIcon from '@mui/icons-material/Check';
// import BasicDetails from './BasicDetails';
// import BudgetDetails from './BudgetDetails';
// import ScopeConfig, { ServiceState } from './ScopeConfig';
// import AlertSettings from './AlertSettings';
// import LabelSuccessToast from './LabelSuccessToast';
// import { LabelFilterPair } from './LabelFilterMenu';

// // --- TYPE DEFINITIONS ---
// interface UserData {
//   id: string;
//   name: string;
//   email: string;
//   role: string;
//   avatar?: string;
// }
// interface RoleData {
//   name: string;
//   description: string;
//   icon: string;
//   color: string;
// }
// type AlertRecipient = {
//   data: { id?: string; name: string; [key: string]: any };
//   type: 'role' | 'user';
// };

// interface Tag {
//   label: string;
//   value: string;
//   id: string | number;
// }

// interface AppOutletContext {
//   currentTenant?: { id: string; name: string; };
// }

// const staticRoles: RoleData[] = [
//   { name: 'Admin', description: 'All current/ new users with admin role', icon: '/icons/admin.svg', color: '#FF8900' },
//   { name: 'Editor', description: 'All current/ new users with editor role', icon: '/icons/editor.svg', color: '#49A7E4' },
//   { name: 'Viewer', description: 'All current/ new users with viewer role', icon: '/icons/viewer.svg', color: '#666666' },
// ];

// // --- QUERIES AND MUTATIONS ---
// const GET_BUDGET_BY_ID = gql`
//   query GetBudgetById($id: ID!) {
//     budgetById(id: $id) {
//       id
//       name
//       description
//       tags
//       provider
//       period
//       amount
//       includeCredits
//       alertThresholds
//       userIdsToNotify
//       scopeConfigurations {
//         id
//         dataSources { id }
//         projectIds
//         regions
//         services
//         labels
//         k8sClusters
//         k8sNamespaces
//       }
//     }
//   }
// `;

// const GET_USERS_BY_ROLE = gql`
//   query GetUsersByRoleInTenant($tenantId: ID!, $roles: [Role!]!) {
//     getUsersByRoleInTenant(tenantId: $tenantId, roles: $roles) {
//       id
//       name
//       email
//       role
//       photo
//     }
//   }
// `;

// const UPDATE_BUDGET_MUTATION = gql`
//   mutation UpdateBudget($id: ID!, $input: UpdateBudgetInput!) {
//     updateBudget(id: $id, input: $input) {
//       id
//       name
//     }
//   }
// `;

// const getInitialService = (): Omit<ServiceState, 'id' | 'serviceTypes'> & { serviceTypes: string[] } => ({
//   dataSources: [],
//   projectIds: [],
//   regions: [],
//   serviceTypes: [],
//   label: [],
//   cluster: [],
//   namespace: [],
//   deployment: []
// });

// const EditBudgetPage: React.FC = () => {
//   const navigate = useNavigate();
//   const { budgetId } = useParams<{ budgetId: string }>();
//   const { currentTenant } = useOutletContext<AppOutletContext>();
//   const paperRef = useRef(null);
//   const client = useApolloClient();

//   // --- STATE MANAGEMENT ---
//   const [errors, setErrors] = useState<{ [key: string]: string }>({});
//   const [budgetName, setBudgetName] = useState('');
//   const [budgetDescription, setBudgetDescription] = useState('');
//   const [tags, setTags] = useState<Tag[]>([]);
//   const [provider, setProvider] = useState('');
//   const [services, setServices] = useState<ServiceState[]>([]);
//   const [period, setPeriod] = useState<'Monthly' | 'Quarterly' | 'Yearly'>('Monthly');
//   const [currency, setCurrency] = useState<'INR' | 'USD'>('USD');
//   const [amount, setAmount] = useState('');
//   const [includeCredits, setIncludeCredits] = useState(false);
//   const [thresholds, setThresholds] = useState<{ id: number; percentage: string; }[]>([]);
//   const [alertRecipients, setAlertRecipients] = useState<AlertRecipient[]>([]);
//   const [isDialogOpen, setIsDialogOpen] = useState(false);
//   const [dialogLabel, setDialogLabel] = useState('');
//   const [dialogTag, setDialogTag] = useState('');
//   const [isToastOpen, setIsToastOpen] = useState(false);
//   const [lastDeletedTag, setLastDeletedTag] = useState<Tag | null>(null);
//   const [toastMessage, setToastMessage] = useState('');
//   const [allUsers, setAllUsers] = useState<UserData[]>([]);
//   const [usersLoading, setUsersLoading] = useState(true);

//   // --- DATA FETCHING & PROCESSING ---

//   useEffect(() => {
//     if (!currentTenant?.id) return;
//     const fetchAllUsers = async () => {
//       setUsersLoading(true);
//       const rolesToFetch = ['admin', 'editor', 'viewer'];
//       try {
//         const promises = rolesToFetch.map(role =>
//           client.query({
//             query: GET_USERS_BY_ROLE,
//             variables: { tenantId: currentTenant.id, roles: [role] }
//           })
//         );
//         const results = await Promise.all(promises);
//         const combinedUsers = results.flatMap(result => result.data.getUsersByRoleInTenant);
//         const uniqueUsers = Array.from(new Map(combinedUsers.map(user => [user.id, {
//     ...user,
//     avatar: user.photo
// }])).values());
//         setAllUsers(uniqueUsers);
//       } catch (error) {
//         console.error("Failed to fetch users for dropdown:", error);
//         setAllUsers([]);
//       } finally {
//         setUsersLoading(false);
//       }
//     };
//     fetchAllUsers();
//   }, [currentTenant?.id, client]);

//   const { data: budgetData, loading: queryLoading, error: queryError } = useQuery(GET_BUDGET_BY_ID, {
//     variables: { id: budgetId },
//     skip: !budgetId,
//     onCompleted: (data) => {
//       if (data && data.budgetById) {
//         const budgetToEdit = data.budgetById;
//         setBudgetName(budgetToEdit.name || '');
//         setBudgetDescription(budgetToEdit.description || '');
//         const tagsArray = budgetToEdit.tags
//           ? Object.entries(budgetToEdit.tags).map(([label, value], index): Tag => ({
//               id: `loaded_${label}_${index}`,
//               label,
//               value: String(value),
//             }))
//           : [];
//         setTags(tagsArray);
//         setProvider(budgetToEdit.provider || '');
        
//         const mappedServices = budgetToEdit.scopeConfigurations.map((sc: any, index: number) => {
//             const groupedLabels = (sc.labels || []).reduce((acc: {[key: string]: string[]}, label: {key: string, value: string}) => {
//                 if (!acc[label.key]) acc[label.key] = [];
//                 acc[label.key].push(label.value);
//                 return acc;
//             }, {});

//             const serviceLabels = Object.entries(groupedLabels).map(([key, values], idx) => ({
//                 id: `${key}_${idx}`, key, values, source: 'existing'
//             }));

//             return {
//                 id: sc.id || `service_${Date.now() + index}`,
//                 dataSources: sc.dataSources?.map((ds: { id: string }) => ds.id) || [],
//                 projectIds: sc.projectIds || [],
//                 regions: sc.regions || [],
//                 serviceTypes: sc.services || [],
//                 label: serviceLabels,
//                 cluster: sc.k8sClusters || [],
//                 namespace: sc.k8sNamespaces || [],
//                 deployment: []
//             };
//         });

//         setServices(mappedServices);
//         setPeriod(budgetToEdit.period?.charAt(0).toUpperCase() + budgetToEdit.period?.slice(1).toLowerCase() as 'Monthly' | 'Quarterly' | 'Yearly' || 'Monthly');
//         setAmount(budgetToEdit.amount?.toString() || '');
//         setIncludeCredits(budgetToEdit.includeCredits || false);
//         setThresholds(budgetToEdit.alertThresholds?.map((p: any, i: number) => ({ id: Date.now() + i, percentage: p.toString() })) || []);
//       } else {
//         navigate('/budget-alerts');
//       }
//     }
//   });

//   useEffect(() => {
//     const savedRecipientIds = budgetData?.budgetById?.userIdsToNotify;
//     if (savedRecipientIds && allUsers.length > 0) {
//       const initialRecipients = savedRecipientIds.map((idOrRole: string) => {
//         if (idOrRole.startsWith('ROLE:')) {
//           const roleName = idOrRole.split(':')[1];
//           const roleData = staticRoles.find(
//             (r) => r.name.toLowerCase() === roleName.toLowerCase()
//           );
//           return roleData ? { type: 'role' as const, data: roleData } : null;
//         } else {
//           const userData = allUsers.find(u => u.id === idOrRole);
//           return userData ? { type: 'user' as const, data: userData } : null;
//         }
//       }).filter((item: AlertRecipient | null): item is AlertRecipient => item !== null);
      
//       setAlertRecipients(initialRecipients);
//     }
//   }, [budgetData, allUsers]);

//   const [updateBudget, { loading: mutationLoading }] = useMutation(UPDATE_BUDGET_MUTATION);

//   // --- HANDLER FUNCTIONS ---
//   const isBudgetDetailsComplete = period && amount.trim() !== '';

//   useEffect(() => {
//     const newErrors = { ...errors };
//     let hasErrorChanged = false;

//     if (amount.trim() && newErrors.amount) {
//         delete newErrors.amount;
//         hasErrorChanged = true;
//     }
//     if (provider && newErrors.provider) {
//         delete newErrors.provider;
//         hasErrorChanged = true;
//     }
//     if (services.length > 0 && (newErrors.dataSources || newErrors.regions || newErrors.services)) {
//         delete newErrors.dataSources;
//         delete newErrors.regions;
//         delete newErrors.services;
//         hasErrorChanged = true;
//     }
//     if (alertRecipients.length > 0 && newErrors.alertRecipients) {
//         delete newErrors.alertRecipients;
//         hasErrorChanged = true;
//     }

//     if (hasErrorChanged) {
//         setErrors(newErrors);
//     }
//   }, [amount, provider, services, alertRecipients, tags, errors]);

//   const handleBudgetNameChange = (name: string) => {
//     setBudgetName(name);
//     const newErrors = { ...errors };

//     if (newErrors.budgetName) {
//         delete newErrors.budgetName;
//     }

//     if (!/^[a-zA-Z0-9\s_-]*$/.test(name)) {
//       newErrors.budgetName = 'Name can only contain letters, numbers, spaces, -, and _.';
//     }
//     setErrors(newErrors);
//   };

//   const handleBudgetDescriptionChange = (description: string) => {
//     setBudgetDescription(description);
//     const newErrors = { ...errors };

//     if(newErrors.budgetDescription) {
//         delete newErrors.budgetDescription;
//     }

//     if (description.length > 200) {
//       newErrors.budgetDescription = 'Description cannot exceed 200 characters.';
//     }
//     setErrors(newErrors);
//   };

//   const validateForm = () => {
//     const newErrors: { [key: string]: string } = {};

//     const trimmedName = budgetName.trim();
//     if (!trimmedName) {
//       newErrors.budgetName = 'Budget Name is required.';
//     } else if (trimmedName.length < 3) {
//       newErrors.budgetName = 'Name must be at least 3 characters.';
//     } else if (trimmedName.length > 50) {
//       newErrors.budgetName = 'Name cannot exceed 50 characters.';
//     } else if (!/^[a-zA-Z0-9\s_-]+$/.test(trimmedName)) {
//       newErrors.budgetName = 'Name can only contain letters, numbers, spaces, -, and _.';
//     }

//     const trimmedDescription = budgetDescription.trim();
//     if (!trimmedDescription) {
//       newErrors.budgetDescription = 'Budget Description is required.';
//     } else if (trimmedDescription.length > 200) {
//       newErrors.budgetDescription = 'Description cannot exceed 200 characters.';
//     }

//     if (!provider) newErrors.provider = 'A Cloud Provider must be selected.';
//     if (services.length === 0 || !services.every(s => s.dataSources.length > 0)) newErrors.dataSources = 'Each service must have a Data Source.';
//     if (services.length === 0 || !services.every(s => s.regions.length > 0)) newErrors.regions = 'Each service must have a Region.';
//     if (services.length === 0 || !services.every(s => s.serviceTypes.length > 0)) newErrors.services = 'Each service must have a Service Type configured.';
//     if (!amount.trim()) newErrors.amount = 'Budget Amount is required.';
//     if (alertRecipients.length === 0) newErrors.alertRecipients = 'At least one Alert Recipient is required.';

//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   const handleSaveChanges = async () => {
//     if (!validateForm() || !budgetId) return;

//     const updateBudgetInput = {
//       name: budgetName,
//       description: budgetDescription,
//       tags: tags.reduce((obj, item) => ({ ...obj, [item.label]: item.value }), {}),
//       period: period.toUpperCase() as 'MONTHLY' | 'QUARTERLY' | 'YEARLY',
//       amount: parseFloat(amount.replace(/,/g, '')),
//       includeCredits: includeCredits,
//       alertThresholds: thresholds.map(t => parseInt(t.percentage, 10)).filter(t => !isNaN(t)),
//       userIdsToNotify: alertRecipients.map(recipient => {
//         if (recipient.type === 'role') {
//           return `ROLE:${recipient.data.name.toLowerCase()}`;
//         }
//         return recipient.data.id as string;
//       }),
//       scopeConfigurations: services.map(service => ({
//         dataSourceIds: service.dataSources,
//         projectIds: service.projectIds,
//         regions: service.regions,
//         services: service.serviceTypes,
//         labels: service.label.flatMap((l: LabelFilterPair) =>
//             l.values.map((v: string) => ({ key: l.key, value: v }))
//         ),
//         k8sClusters: service.cluster,
//         k8sNamespaces: service.namespace,
//       })),
//     };

//     try {
//       const result = await updateBudget({
//         variables: { id: budgetId, input: updateBudgetInput },
//       });

//       if (result.data?.updateBudget) {
//         navigate('/budget-alerts', {
//           replace: true,
//           state: { budgetUpdated: true, budgetName: result.data.updateBudget.name }
//         });
//       } else {
//         throw new Error("Received no data from server after update.");
//       }
//     } catch (err: any) {
//       console.error("Failed to update budget:", err);
//       setToastMessage(`Error: ${err.message || 'Could not save changes.'}`);
//       setIsToastOpen(true);
//     }
//   };

//   const handleAddTag = () => {
//     const newTag: Tag = { id: Date.now(), label: dialogLabel, value: dialogTag };
//     if (newTag.label && newTag.value && !tags.some(tag => tag.label === newTag.label && tag.value === newTag.value)) {
//       setTags(prevTags => [...prevTags, newTag]);
//     }
//     setIsDialogOpen(false);
//     setDialogLabel('');
//     setDialogTag('');
//   };

//   const handleDeleteTag = (tagToDelete: Tag) => {
//     setLastDeletedTag(tagToDelete);
//     setTags(prevTags => prevTags.filter(tag => tag.id !== tagToDelete.id));
//     setToastMessage("Label deleted successfully");
//     setIsToastOpen(true);
//   };

//   const handleUndo = () => {
//     if (lastDeletedTag) {
//       setTags(prevTags => [...prevTags, lastDeletedTag]);
//       setLastDeletedTag(null);
//       setIsToastOpen(false);
//     }
//   };

//   const handleToastClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
//     if (reason === 'clickaway') return;
//     setIsToastOpen(false);
//     setLastDeletedTag(null);
//   };

//   const handleCloseDialog = () => {
//     setIsDialogOpen(false);
//     setDialogLabel('');
//     setDialogTag('');
//   };

//   if (queryLoading) {
//     return (
//       <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
//         <CircularProgress />
//       </Box>
//     );
//   }

//   if (queryError) {
//     return (
//         <Box sx={{ p: 3, textAlign: 'center' }}>
//             <Typography color="error" variant="h6" gutterBottom>Error Loading Budget</Typography>
//             <Typography color="error">Could not fetch budget details. Please try again later.</Typography>
//             <pre style={{ whiteSpace: 'pre-wrap', textAlign: 'left', backgroundColor: '#fdecea', border: '1px solid #f44336', padding: '1rem', marginTop: '1rem', borderRadius: '8px' }}>
//                 {queryError.message}
//             </pre>
//             <Button sx={{mt: 2}} variant="contained" onClick={() => navigate('/budget-alerts')}>Go Back</Button>
//         </Box>
//     );
//   }

//   return (
//     <Box sx={{ p: { xs: 1, sm: 0 }, height: '100%', boxSizing: 'border-box', backgroundColor: '#F8F9FA' }}>
//       <LabelSuccessToast
//         open={isToastOpen}
//         onClose={handleToastClose}
//         message={toastMessage}
//         onUndo={lastDeletedTag ? handleUndo : () => {}}
//       />
      
//       <Box sx={{ mb: 2 }}>
//         <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: -1 }}>
//           <IconButton onClick={() => navigate('/budget-alerts')} size="small" sx={{ color: '#333333', ml: -1 }}>
//             <ArrowBackIcon sx={{ width: '20px', height: '20px' }} />
//           </IconButton>
//           <Typography variant="body2" color="#333333" sx={{ fontWeight: 600, fontFamily: 'Inter', fontSize: '14px' }}>
//             Edit Budget
//           </Typography>
//         </Box>
//         <Typography variant="body2" color="#666666" sx={{ fontWeight: 400, fontFamily: 'Inter', fontSize: '12px', ml: 2.5, mt: -1 }}>
//           Edit the below details to update the budget
//         </Typography>
//       </Box>
      
//       <Box component="div" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
//         <BasicDetails
//           budgetName={budgetName}
//           onBudgetNameChange={handleBudgetNameChange}
//           budgetDescription={budgetDescription}
//           onBudgetDescriptionChange={handleBudgetDescriptionChange}
//           tags={tags}
//           handleDeleteTag={handleDeleteTag}
//           setIsDialogOpen={setIsDialogOpen}
//           errors={errors}
//         />
//         <BudgetDetails
//           period={period}
//           setPeriod={setPeriod}
//           currency={currency}
//           setCurrency={setCurrency}
//           amount={amount}
//           setAmount={setAmount}
//           includeCredits={includeCredits}
//           setIncludeCredits={setIncludeCredits}
//           thresholds={thresholds}
//           setThresholds={setThresholds}
//           errors={errors}
//         />
//         <ScopeConfig
//           provider={provider}
//           setProvider={setProvider}
//           services={services}
//           setServices={setServices}
//           getInitialService={getInitialService}
//           errors={errors}
//           disabled={!isBudgetDetailsComplete}
//           period={period}
//         />
//         <AlertSettings
//           recipients={alertRecipients}
//           setRecipients={setAlertRecipients}
//           errors={errors}
//           roles={staticRoles}
//           allUsers={allUsers}
//           isLoading={usersLoading || queryLoading}
//         />
//         <Box sx={{ p: 2, bgcolor: '#FFFFFF', borderRadius: '10px', border: '1px solid #F3F3F3', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
//           <Box sx={{ display: 'flex', gap: 1 }}>
//             <Button onClick={() => navigate('/budget-alerts')} sx={{ width: '79px', height: '36px', borderRadius: '8px', backgroundColor: '#F2F7FF', color: '#0064FF', fontFamily: 'Inter', fontWeight: 500, fontSize: '14px', textTransform: 'none', '&:hover': { backgroundColor: '#E0EFFF' } }}>
//               Cancel
//             </Button>
//             <Button
//               variant="contained"
//               onClick={handleSaveChanges}
//               disabled={mutationLoading}
//               sx={{ width: '130px', height: '36px', borderRadius: '8px', backgroundColor: '#0064FF', color: 'white', fontFamily: 'Inter', fontWeight: 500, fontSize: '14px', textTransform: 'none', '&:hover': { backgroundColor: '#0053D9' } }}
//             >
//               {mutationLoading ? <CircularProgress size={24} color="inherit" /> : 'Save Changes'}
//             </Button>
//           </Box>
//         </Box>
//       </Box>
//       <Dialog
//         open={isDialogOpen}
//         onClose={handleCloseDialog}
//         PaperProps={{
//           ref: paperRef,
//           sx: {
//             borderRadius: '12px', width: '600px', height: '322px', maxWidth: '600px', overflow: 'hidden',
//             backgroundImage: `url('/textures/Texture.svg'), url('/textures/Gradient_Popup.svg')`,
//             backgroundRepeat: 'no-repeat, no-repeat', backgroundPosition: 'top right, center', backgroundSize: 'auto, cover',
//           }
//         }}
//       >
//        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
//           <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
//             <img src="/icons/add-filter-icon1.svg" alt="Add" style={{ width: '56px', height: '56px', marginRight: '16px' }} />
//             <Box>
//               <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '20px', color: '#333333', fontFamily: 'Inter' }}>Add Label/ Tag</Typography>
//               <Typography variant="body2" sx={{ fontWeight: 400,fontSize: '14px', color: '#666666', fontFamily: 'Inter' }}>Add label details to setup alerts.</Typography>
//             </Box>
//           </Box>
//           <DialogContent sx={{ p: '0 !important', mt: 7, mb: 3, flexGrow: 1 }}>
//             <Box sx={{ display: 'flex', gap: 2 }}>
//               <Box sx={{ flex: 1 }}>
//                 <Typography variant="body2" sx={{ mb: 1, fontFamily: 'Inter', fontSize: '14px',fontWeight: 400 }}>Key<span style={{color: 'red'}}>*</span></Typography>
//                 <TextField fullWidth variant="outlined" placeholder="Enter label" value={dialogLabel} onChange={(e) => setDialogLabel(e.target.value)} size="small" InputProps={{ endAdornment: ( <InputAdornment position="end"> {dialogLabel && ( <CheckIcon sx={{ color: '#03A65A', fontSize: '15px' }} /> )} </InputAdornment> ) }} sx={{ '& .MuiOutlinedInput-root': { height: '40px', borderRadius: '8px', fontFamily: 'Inter', backgroundColor: '#FFFFFF', '& .MuiInputBase-input:-webkit-autofill': { WebkitBoxShadow: '0 0 0 100px #FFFFFF inset' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#E0E0E0', }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0064FF',borderWidth: '1px' } }, '& .MuiInputBase-input::placeholder': { fontSize: '14px', fontFamily: 'Inter', }, '& .MuiInputBase-input': { fontFamily: 'Inter', fontSize: '14px', }, }}/>
//               </Box>
//               <Box sx={{ flex: 1 }}>
//                 <Typography variant="body2" sx={{ mb: 1, fontFamily: 'Inter', fontSize: '14px',fontWeight: 400 }}>Value<span style={{color: 'red'}}>*</span></Typography>
//                 <TextField fullWidth variant="outlined" placeholder="Enter tag" value={dialogTag} onChange={(e) => setDialogTag(e.target.value)} size="small" InputProps={{ endAdornment: ( <InputAdornment position="end"> {dialogTag && ( <CheckIcon sx={{ color: '#03A65A', fontSize: '15px' }} /> )} </InputAdornment> ) }} sx={{ '& .MuiOutlinedInput-root': { height: '40px', borderRadius: '8px', fontFamily: 'Inter', backgroundColor: '#FFFFFF', '& .MuiInputBase-input:-webkit-autofill': { WebkitBoxShadow: '0 0 0 100px #FFFFFF inset' }, '& fieldset': { borderColor: '#E0E0E0', }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#E0E0E0' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0064FF',borderWidth: '1px' } }, '& .MuiInputBase-input::placeholder': { fontSize: '14px', fontFamily: 'Inter', }, '& .MuiInputBase-input': { fontFamily: 'Inter', fontSize: '14px',}, }}/>
//               </Box>
//             </Box>
//           </DialogContent>
//           <DialogActions sx={{ p: 0 }}>
//             <Button onClick={handleCloseDialog} sx={{ width:"79px", height:"36px",textTransform: 'none', borderRadius: '8px', color: '#0064FF', backgroundColor: '#F2F7FF', fontFamily: 'Inter', fontSize:"14px", fontWeight:500 }}>Cancel</Button>
//             <Button variant="contained" onClick={handleAddTag} disabled={!dialogLabel || !dialogTag} sx={{ width:"74px", height:"36px",textTransform: 'none', borderRadius: '8px', backgroundColor: '#0064FF', fontFamily: 'Inter', fontSize:"14px", fontWeight:500 }}> Add </Button>
//           </DialogActions>
//         </Box>
//       </Dialog>
//     </Box>
//   );
// };

// export default EditBudgetPage;